/**
 * Filter Key Constants
 *
 * The query-string keys the list pages send to their index endpoints.
 *
 * Each one is half of a contract: the page writes `?status=...` and the
 * matching Form Request whitelists `status`. Nothing checks the two agree — a
 * page that misspells a key gets a request that validates cleanly and silently
 * ignores the filter, so the list just returns everything and looks like a
 * backend bug. Naming them once at least makes the frontend half consistent
 * and greppable against the rules.
 *
 * Feature-specific key sets that extend these live next to their feature —
 * see `DOCUMENT_FILTER_KEYS` in `rates.ts`, which reuses the shared values
 * below rather than redeclaring them.
 */
export const FILTER_KEYS = {
    /** Free-text search box. Present on nearly every list page. */
    SEARCH: 'search',
    /** `key:direction` sort pair — see `formatSortParam` in `types/table.ts`. */
    SORT: 'sort',
    /** Paginator page number. */
    PAGE: 'page',

    STATUS: 'status',
    SOURCE: 'source',
    TYPE: 'type',
    CATEGORY: 'category',
    COUNTRY: 'country',
    REASON: 'reason',
    SUPPLIER: 'supplier',
    /** Which guests a rate sheet is for — `App\Enums\RateMarket` values. */
    MARKET: 'market',

    /** CRM: the user a lead or activity is assigned to. */
    ASSIGNEE: 'assignee',
    /** CRM: pipeline stage. */
    STAGE: 'stage',
    /** CRM: acquisition channel. */
    CHANNEL: 'channel',
    /**
     * CRM: leads whose travel window overlaps `travel_from`..`travel_to`.
     * `LeadBoardFilters` has validated and applied both since the board
     * shipped; the list and board pages just never rendered a control for
     * them.
     */
    TRAVEL_FROM: 'travel_from',
    TRAVEL_TO: 'travel_to',

    /** Network browse: connection state between the two tenants. */
    CONNECTION_STATUS: 'connection_status',
    /** Network browse: whether the supplier has shared rates with us. */
    HAS_RATES_SHARED: 'has_rates_shared',
} as const;

export type FilterKey = (typeof FILTER_KEYS)[keyof typeof FILTER_KEYS];
